import { api } from './client';
import type { Page } from './catalogs';

export type ProjectKind = 'DIRECTION' | 'PROJECT';
export type ProjectStatus = 'PLANNED' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';

export interface Project {
  id: string;
  tenant_id: string;
  code: string;
  name: string;
  full_name: string | null;
  kind: ProjectKind;
  status: ProjectStatus;
  start_date: string | null;
  end_date: string | null;
  manager_id: string | null;
  description: string | null;
  is_overhead: boolean;
  parent_id: string | null;
  deletion_mark: boolean;
  external_source?: string | null;
}

export interface ProjectCreate {
  code: string;
  name: string;
  full_name?: string | null;
  kind?: ProjectKind;
  status?: ProjectStatus;
  start_date?: string | null;
  end_date?: string | null;
  manager_id?: string | null;
  description?: string | null;
  is_overhead?: boolean;
  parent_id?: string | null;
}

export interface ProjectPLLine {
  project_id: string | null;
  project_code: string | null;
  project_name: string | null;
  project_kind: ProjectKind | null;
  income: number;
  expense: number;
  result: number;
  transactions: number;
}

export interface ProjectPLOrgBreakdown {
  organization_id: string;
  organization_code: string;
  organization_name: string;
  currency: string;
  lines: ProjectPLLine[];
  totals: { income: number; expense: number; result: number };
}

export interface ProjectPLReport {
  period_start: string;
  period_end: string;
  organization_ids: string[];
  intragroup_note?: string;
  lines: ProjectPLLine[];
  totals: { income: number; expense: number; result: number };
  // UX γ Y-deep P (2026-05-22): aggregation fields
  breakdown_by_org?: ProjectPLOrgBreakdown[];
  currencies?: string[];
  warnings?: string[];
}

export const projects = {
  list: (params?: {
    organization_id?: string;
    kind?: ProjectKind;
    status?: ProjectStatus;
    limit?: number;
  }) => {
    // Власник 2026-06-02: фільтр пустих значень — backend очікує UUID, а
    // не пустий рядок (інакше 422). Захист на api-рівні, додатково до
    // `enabled` у компонентах.
    const clean = params
      ? Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== '' && v !== undefined && v !== null),
        )
      : undefined;
    return api.get<Page<Project>>('/projects', { params: clean }).then((r) => r.data);
  },
  get: (id: string) => api.get<Project>(`/projects/${id}`).then((r) => r.data),
  create: (p: ProjectCreate) => api.post<Project>('/projects', p).then((r) => r.data),
  update: (id: string, p: Partial<ProjectCreate>) =>
    api.patch<Project>(`/projects/${id}`, p).then((r) => r.data),
  remove: (id: string) => api.delete(`/projects/${id}`).then(() => undefined),
  pnl: (params: {
    period_start: string;
    period_end: string;
    // Один з трьох режимів:
    // - organization_id: одна юрособа
    // - organization_ids: список
    // - нічого: весь tenant (наскрізний)
    organization_id?: string;
    organization_ids?: string[];
    kind?: ProjectKind;
    include_unassigned?: boolean;
  }) =>
    api.get<ProjectPLReport>('/projects/reports/pnl', { params }).then((r) => r.data),
};
